<script lang="ts">
	import { goto, preloadData } from '$app/navigation';
	import { ClientSettings } from '$lib/ClientSettings';
	import { ETheme } from '$lib/dto/ETheme';
	import type { PlatformBaseUrls } from '$lib/model';
	import { Ellipsis, isEmptyOrWhitespace } from '$lib/svelte-utils/utils/stringUtils';
	import { withBaseUrl } from '$lib/svelte-utils/utils/urlUtils';
	import { appTheme } from '$lib/utils/themeUtils';
	import PlatformLogo from '../base/PlatformLogo.svelte';
	import TinySpinner from '../comment/TinySpinner.svelte';

	export let label = '';
	export let sampleMergeRequestUrl = ClientSettings.DefaultMergeRequestSampleLink;
	export let showSampleAndLabels = true;
	export let baseUrls: PlatformBaseUrls;

	let isLoading = false;

	let reviewUrl = '';

	function handleUrlChange() {
		try {
			const reviewLink = getAppReviewLink(reviewUrl);
			if (reviewLink) {
				isLoading = true;
				goto(reviewLink);
			}
		} catch (e) {
			// TODO: Error handling
		}
	}

	function getAppReviewLink(urlByUser: string) {
		if (!urlByUser) {
			return null;
		}
		const url = new URL(urlByUser);
		switch (url.host) {
			case new URL(baseUrls.gitlab).host:
				const matcher = url.pathname.match(/\/(.*)\/-\/merge_requests\/(\d+)/);
				if (matcher) {
					return '/review/gitlab/' + encodeURIComponent(matcher[1]) + '/' + matcher[2];
				}
				break;
			case new URL(baseUrls.github).host:
				const ghMatcher = url.pathname.match(/([\w-]+)\/([\w-]+)\/pull\/(\d+)/);
				if (ghMatcher) {
					return (
						'/review/github/' +
						encodeURIComponent(ghMatcher[1] + '/' + ghMatcher[2]) +
						'/' +
						ghMatcher[3]
					);
				}
				break;
			default:
				return null;
		}
	}

	function preloadReviewIfValidPullRequestLink(event: ClipboardEvent) {
		if (!event?.clipboardData) {
			return;
		}
		const pastedUrl = event.clipboardData.getData('text/plain');
		const redirectLink = getAppReviewLink(pastedUrl);
		if (redirectLink) {
			preloadData(redirectLink);
		}
	}
</script>

<form on:submit|preventDefault={() => handleUrlChange()}>
	<div class="flex flex-wrap items-center">
		<label class="flex-1 font-bold" for="review-url">
			{#if label}
				{label}
			{:else}
				Your {reviewUrl && reviewUrl.includes('github') ? 'pull' : 'merge'} request URL
			{/if}</label
		>
		<div class:hidden={!showSampleAndLabels} class="text-secondary flex items-center gap-x-4">
			Works with
			<PlatformLogo class="h-5" platform="gitlab" />
			<PlatformLogo class="h-5" useBlackIcon={$appTheme === ETheme.Light} platform="github" />
		</div>
	</div>
	<div class="mt-4 flex">
		<input
			bind:value={reviewUrl}
			on:paste={preloadReviewIfValidPullRequestLink}
			id="review-url"
			class="flex-1 rounded-none !rounded-r-none rounded-l-lg bg-gray-200 text-lg focus:bg-white dark:bg-inherit dark:[&:not(:focus)]:!border-[#333]"
			type="url"
			placeholder={withBaseUrl(baseUrls.gitlab, '/me/myproject/-/merge_requests/42')}
		/>
		<button
			disabled={isLoading}
			class="button flex items-center gap-x-2 !rounded-none !rounded-r-lg"
			class:theme-primary={!isEmptyOrWhitespace(reviewUrl)}
			type="submit"
		>
			{#if isLoading}
				<TinySpinner />
				<span>Loading{Ellipsis}</span>
			{:else}
				<span>Review</span>
			{/if}
		</button>
	</div>
	{#if sampleMergeRequestUrl && showSampleAndLabels}
		<div class="py-3 text-blue-500 hover:text-blue-600">
			<button
				type="button"
				on:mouseenter={() => {
					const redirectLink = getAppReviewLink(sampleMergeRequestUrl);
					if (redirectLink) {
						preloadData(redirectLink);
					}
				}}
				on:click={() => {
					reviewUrl = sampleMergeRequestUrl;
					handleUrlChange();
				}}>View example</button
			>
		</div>
	{/if}
</form>
